import { useState, useEffect, useCallback, useRef } from 'react'
import { message } from 'antd'

export const usePrescribedTreatments = ({ 
    apiType,
    source,
    caseDetails, 
    activeRequest = null, 
    apiClasses 
}) => {
    const [prescriptions, setPrescriptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const abortController = useRef(null)

    const getPharmacyForRx = useCallback(async (rx, signal) => {
        if (!apiClasses?.PrescriptionsAPI) {
            return { ...rx, pharmacy: null }
        }
        
        try {
            const { data } = await apiClasses.PrescriptionsAPI.getPharmacybyId(rx.pharmacyId, { signal })
            return { ...rx, pharmacy: data.pharmacy }
        } catch (e) {
            if (e.name === 'AbortError') throw e
            return { ...rx, pharmacy: null }
        }
    }, [apiClasses])

    const getRxProvider = useCallback(async (rx) => {
        if (!apiClasses?.PhysicianAPI) {
            return  
        }

        if (!rx.externalProviderId || rx.pharmacyStatus === 'Error') {
            return
        }
        
        const { data } = apiType === 'admin' 
            ? await apiClasses.PhysicianAPI.getPhysiciansV2({ id: rx.externalProviderId }) 
            : await apiClasses.PhysicianAPI.getPhysicians({ id: rx.externalProviderId })

        return data.payload || data.physicians[0]
    }, [apiClasses, apiType])
    
    const fetchPrescriptionHistory = useCallback(async () => {
        if (!apiClasses?.PrescriptionsAPI || !caseDetails?.patientId) {
            setPrescriptions([])
            return
        }

        if (!activeRequest) return

        abortController.current?.abort()
        abortController.current = new AbortController()
        
        setLoading(true)
        setError(null)

        // history of all patient's requests sorted in descending order by createdAt
        const history = caseDetails.requests || []
        
        try {

            // creates window where start time is the date of creation of the active request and the end time is the date of creation of the most recent request after the active request
            const rxWindow = {}

            const activeRequestIndex = history.map(request => request._id).indexOf(activeRequest._id) || 0
            const mostRecentRequestAfterActiveRequest = history[activeRequestIndex + 1]

            if (source === 'request') {
                rxWindow.startDate = activeRequest.createdAt
                rxWindow.endDate = mostRecentRequestAfterActiveRequest?.createdAt || new Date().toISOString()
            }

            const signal = abortController.current.signal
            
            const { data: { prescriptions } } = await apiClasses.PrescriptionsAPI.getPatientPrescriptionHistory(
                caseDetails.patientId, 
                rxWindow,
                { signal }
            )

            const prescribedMedications = prescriptions?.filter(rx => 
                rx.prescriberStatus?.toLowerCase() !== 'deleted'
            ) || []
        
            const prescriptionsWithPharmacies = await Promise.all(
                prescribedMedications.map(rx => getPharmacyForRx(rx, signal))
            )

            const providerMap = new Map()

            for (const rx of prescriptionsWithPharmacies) {
                if (!rx.externalProviderId) {
                    continue
                }
                
                if (!providerMap.has(rx.externalProviderId)) {
                    const provider = await getRxProvider(rx)
                    providerMap.set(rx.externalProviderId, provider)
                    rx.provider = provider
                } else {
                    rx.provider = providerMap.get(rx.externalProviderId)
                }
            }

            if (!signal?.aborted) setPrescriptions(prescriptionsWithPharmacies)
        } catch (e) {
            if (e.name === 'AbortError' || abortController.current?.signal.aborted) {
                return
            }
            
            setError('Error fetching prescription history')
            setPrescriptions([])
        } finally {
            setLoading(false)
        }
    }, [caseDetails, activeRequest, apiClasses, source, getPharmacyForRx, getRxProvider])
    
    const handleCancelPrescription = useCallback(async (prescriptionId, providerId) => {
        if (!apiClasses?.PrescriptionsAPI) {
            return false
        }
        
        try {
            const response = await apiClasses.PrescriptionsAPI.cancelPrescription(prescriptionId, providerId)
            
            if (response.data?.statusCode === 200 && response.data?.error === false) {
                setPrescriptions(prev => prev.map(rx => 
                    rx.prescriptionId !== prescriptionId 
                        ? rx 
                        : { ...rx, medicationStatus: 'CancelPending' }
                ))
                return true
            } else {
                throw new Error('Error cancelling rx request prescription')
            }
        } catch (e) {
            message.error('Error cancelling prescription')
            return false
        }
    }, [apiClasses])
    
    const handleDeletePrescription = useCallback(async (prescriptionId, providerId) => {
        if (!apiClasses?.PrescriptionsAPI) {
            return false
        }
        
        try {
            const response = await apiClasses.PrescriptionsAPI.deletePrescription(prescriptionId, providerId)
            
            if (response.data?.statusCode === 200 && response.data?.error === false) {
                setPrescriptions(prev => prev.filter(rx => rx.prescriptionId !== prescriptionId))
                return true
            } else {
                throw new Error('Error deleting prescription')
            }
        } catch (e) {
            message.error('Error deleting prescription')
            return false
        }
    }, [apiClasses])
    
    const handleResendPrescription = useCallback(async ({ prescriptionId, PIN, providerId }) => {
        if (!apiClasses?.PrescriptionsAPI) {
            return false
        }
        
        try {
            const response = await apiClasses.PrescriptionsAPI.resendPrescription({ prescriptionId, providerId, PIN })
            
            if (response.data?.statusCode === 200 && response.data?.result?.success === true) {
                return true
            } else {
                throw new Error('Error re-sending prescription')
            }
        } catch (e) {
            message.error('Error resending prescription')
            return false
        }
    }, [apiClasses])
    
    useEffect(() => {
        fetchPrescriptionHistory()
        
        return () => {
            if (abortController.current) {
                abortController.current.abort()
            }
        }
    }, [fetchPrescriptionHistory])
    
    return {
        prescriptions,
        loading,
        error,
        fetchPrescriptionHistory,
        handleCancelPrescription,
        handleDeletePrescription,
        handleResendPrescription
    }
}
